import React, { useEffect, useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	TextField,
	Button,
	Box,
} from '@mui/material';
import { VpnKeyRounded } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { apiErrorMessage } from '../../../api/catalog';

const ModalActions = styled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'flex-end',
	gap: theme.spacing(1.5),
	padding: theme.spacing(2, 3, 3),
}));

interface IngresarCodigoDialogProps {
	open: boolean;
	onClose: () => void;
	onConfirmar: (code: string) => Promise<void>;
}

const IngresarCodigoDialog: React.FC<IngresarCodigoDialogProps> = ({
	open,
	onClose,
	onConfirmar,
}) => {
	const [codigo, setCodigo] = useState('');
	const [localError, setLocalError] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) {
			setCodigo('');
			setLocalError(null);
			setSaving(false);
		}
	}, [open]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setCodigo(e.target.value.toUpperCase());
		setLocalError(null);
	};

	const handleConfirmar = async () => {
		const trimmed = codigo.trim();
		if (!trimmed) {
			setLocalError('Ingresa un código para continuar.');
			return;
		}
		if (saving) return;
		setSaving(true);
		setLocalError(null);
		try {
			await onConfirmar(trimmed);
			setCodigo('');
		} catch (err) {
			setLocalError(apiErrorMessage(err));
			setSaving(false);
		}
	};

	const handleClose = () => {
		if (saving) return;
		setCodigo('');
		setLocalError(null);
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
			<DialogTitle>Ingresar código de materia</DialogTitle>

			<DialogContent>
				<DialogContentText>
					Ingresa el código que te compartió tu docente para acceder a los recursos de la materia.
				</DialogContentText>

				<TextField
					fullWidth
					label='Código de materia'
					placeholder='Ej. CDI-7K2P'
					value={codigo}
					onChange={handleChange}
					error={!!localError}
					helperText={localError ?? 'El código no distingue mayúsculas.'}
					margin='normal'
					inputProps={{ style: { fontFamily: 'monospace', letterSpacing: '0.1em', fontWeight: 700 } }}
					onKeyDown={(e) => {
						if (e.key === 'Enter') void handleConfirmar();
					}}
				/>
			</DialogContent>

			<ModalActions>
				<Button variant='outlined' onClick={handleClose} disabled={saving}>
					Cancelar
				</Button>
				<Button
					variant='contained'
					onClick={() => void handleConfirmar()}
					disabled={!codigo.trim() || saving}
					startIcon={<VpnKeyRounded />}
				>
					{saving ? 'Verificando...' : 'Acceder a materia'}
				</Button>
			</ModalActions>
		</Dialog>
	);
};

export default IngresarCodigoDialog;
